import React, { FC, Fragment, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import { Alert, Box, Checkbox, Grid, InputAdornment, TextField } from '@mui/material';
import { CurrentJobs } from 'models/organization';
import InfoText from 'components/shared/info-text.component';

type AvailableConstructionsProps = {
    exports: {
        enable: boolean;
        additionalDeliveryDays: number;
    };
    currentJobs: CurrentJobs[];
    onJobChange: (index: number) => (event: ChangeEvent<HTMLInputElement>) => void;
    onAdditionalDaysChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onProductionAbroadChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onDeliveryDaysChange: (index: number) => (event: ChangeEvent<HTMLInputElement>) => void;
};

const AvailableConstructions: FC<AvailableConstructionsProps> = ({
    exports,
    currentJobs,
    onJobChange,
    onAdditionalDaysChange,
    onProductionAbroadChange,
    onDeliveryDaysChange,
}: AvailableConstructionsProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    return (
        <Fragment>
            <PageTitle headingStyle="h3" title={translate('lab-settings:AVAILABLE_CONSTRUCTIONS')} />
            <Box mb={2}>
                <InfoText text={translate('lab-settings:AVAILABLE_CONSTRUCTIONS_INFO')} />
            </Box>

            <Box mb={3}>
                {currentJobs?.map((construction, index) => (
                    <Grid container key={construction?.name} sx={{ marginBottom: 1 }}>
                        <Grid item xs={3}>
                            <Box display="flex" alignItems="center">
                                <Checkbox checked={construction.active} onChange={onJobChange(index)} />
                                <Box>{translate(`lab-settings:${construction?.name?.toUpperCase()}`)}</Box>
                            </Box>
                        </Grid>
                        <Grid item xs={1}>
                            {construction.active ? (
                                <TextField
                                    fullWidth
                                    sx={{ width: '120px' }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {translate('lab-settings:DAYS')}
                                            </InputAdornment>
                                        ),
                                    }}
                                    value={currentJobs[index].deliveryDays || ''}
                                    onChange={onDeliveryDaysChange(index)}
                                    disabled={!construction.active}
                                />
                            ) : undefined}
                        </Grid>
                    </Grid>
                ))}
            </Box>

            <PageTitle headingStyle="h3" title={translate('lab-settings:PRODUCTION_ABROAD')} />
            <Box mb={2}>
                <InfoText text={translate('lab-settings:PRODUCTION_ABROAD_INFO')} />
            </Box>
            <Grid container sx={{ marginBottom: 1 }}>
                <Grid item xs={3}>
                    <Box display="flex" alignItems="center">
                        <Checkbox checked={exports.enable} onChange={onProductionAbroadChange} />
                        <Box>{translate('lab-settings:PRODUCTION_ABROAD')}</Box>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    {exports.enable ? (
                        <TextField
                            fullWidth
                            sx={{ width: '180px' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {translate('lab-settings:ADDITIONAL_DAYS')}
                                    </InputAdornment>
                                ),
                            }}
                            value={exports.additionalDeliveryDays || ''}
                            onChange={onAdditionalDaysChange}
                            disabled={!exports.enable}
                            hidden={!exports.enable}
                        />
                    ) : undefined}
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default AvailableConstructions;
